/**
 * `PurchaseCreditsModal` — the reusable "sell or comp a package / membership"
 * sub-UI, extracted from `Calendar.tsx`'s `SlotModal` so the SAME machinery
 * backs BOTH the calendar's no-credit remedy AND the member page's Purchase
 * button. It is DECOUPLED from any slot context: it only needs a `memberId`.
 *
 * Three flows it dispatches:
 *
 *   - **Grant free** — `staff_create_member_package` (comp package),
 *     `staff_create_member_membership` (one-time comp membership), or
 *     `grant_recurring_membership` (free £0-Stripe auto-refilling membership).
 *   - **Create payment link** — `create_payment_link` (catalog OR custom
 *     amount): a 30-day link of Layer's own that mints a fresh Stripe Session
 *     on every tap, so it does not rot in an inbox the way the old 60-minute
 *     `charge_member` Session did,
 *     transparently registering the Stripe customer first
 *     (`register_stripe_customer`) when a brand-new member has none.
 *   - **Record sale** — `record_manual_sale` (catalog OR custom amount +
 *     credits): books the money already taken AND grants the credit in one
 *     canonical action, for a sale settled outside Stripe Checkout.
 *
 * A custom PACK sold by link or recorded sale carries a "Valid for (days)"
 * box (custom-pack-validity-at-sale, 2026-10-30), pre-filled by the size rule
 * the grant itself applies (`suggestedCustomValidityDays`), so a 12-credit
 * pack no longer inherits its one-credit carrier's 30 days.
 *
 * Slot coupling is an OPTIONAL `autoBook` prop: when present (the calendar),
 * a successful free GRANT immediately books the member into the slot via
 * `book_session` and hands the run id back to the caller to poll. When
 * absent (the member page), a grant just adds credits and calls `onPurchased`.
 * A paid "create payment link" never auto-books — it shows the URL to send.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  PACKAGE_VALIDITY_DAYS_MAX,
  PACKAGE_VALIDITY_DAYS_MIN,
  resolvePackageValidityDays,
  type PackageValidityOffering,
} from "@layer/canonical-data/package-validity";
import { api } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";

/** A `{value,label}` option from the `/api/lookups/:entity` catalog endpoint. */
export interface CatalogOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
  /** Catalog price (minor units) — used to pre-fill the manual-sale amount. */
  readonly price_minor?: number;
  readonly currency?: string;
  /**
   * Packages only — the size, validity and session type the custom pack's
   * "Valid for (days)" pre-fill reads (custom-pack-validity-at-sale).
   */
  readonly sessions_total?: number;
  readonly validity_days?: number;
  readonly session_type?: string;
}

/**
 * How the purchase is settled — ONE choice covering all five ways, rather than
 * the three separate controls this used to be (two verb buttons in one row, a
 * three-way channel toggle in another, and a third button to commit it).
 *
 * They were never three kinds of decision. "Send them a Stripe link", "comp it"
 * and "they paid cash" are five answers to a single question — *how is this
 * being paid for?* — so they are five peers in one control, and one button
 * commits whichever is chosen.
 */
const PAY_METHODS = [
  "link",
  "free",
  "unpaid",
  "cash",
  "bank_transfer",
  "other",
] as const;
type PayMethod = (typeof PAY_METHODS)[number];

/** The subset that books money already taken outside Stripe. */
const MANUAL_PAY_METHODS = ["cash", "bank_transfer", "other"] as const;
type ManualPayMethod = (typeof MANUAL_PAY_METHODS)[number];

/**
 * A membership can only be paid for by link or comped.
 *
 * The manual methods record money ALREADY taken — a single payment, settled.
 * That is a coherent thing to say about a package (a one-off block of
 * credits) and an incoherent one about a membership, which is a recurring
 * Stripe subscription: recording last month's cash sets up no billing, so the
 * "membership" it grants would silently never renew.
 *
 * `unpaid` is excluded for the same reason from the other direction: a debt is
 * a fixed amount owed for a specific block of sessions, and a membership has
 * no such amount — it bills again next month whatever happens.
 */
const MEMBERSHIP_PAY_METHODS = ["link", "free"] as const;

function payMethodsFor(
  kind: "package" | "membership",
): ReadonlyArray<PayMethod> {
  return kind === "membership" ? MEMBERSHIP_PAY_METHODS : PAY_METHODS;
}

function isManualPayMethod(m: PayMethod): m is ManualPayMethod {
  return (MANUAL_PAY_METHODS as ReadonlyArray<PayMethod>).includes(m);
}

/** Static labels; `free` names the thing being granted, so it is derived. */
const PAY_METHOD_LABEL: Record<Exclude<PayMethod, "free">, string> = {
  link: "Create payment link",
  unpaid: "Not paid yet",
  cash: "Cash",
  bank_transfer: "Bank transfer",
  other: "Other",
};

function payMethodLabel(
  m: PayMethod,
  kind: "package" | "membership",
): string {
  return m === "free" ? `Grant free ${kind}` : PAY_METHOD_LABEL[m];
}

/** A Stripe coupon from `list_stripe_coupons` (for the payment-link discount picker). */
interface CouponOption {
  readonly coupon_id: string;
  readonly name: string | null;
  readonly percent_off: number | null;
  readonly amount_off: number | null;
  readonly currency: string | null;
  readonly duration: string;
  readonly duration_in_months: number | null;
  readonly valid: boolean;
  readonly applies_to: string | null;
  /** 2026-08-14 — item scope; null = the whole class. */
  readonly applies_to_slugs: ReadonlyArray<string> | null;
  readonly max_redemptions: number | null;
  /** 2026-08-14 — per-member cap; null = unlimited. */
  readonly max_redemptions_per_member: number | null;
  readonly times_redeemed: number;
  readonly member_id: string | null;
}

/**
 * What a code may be spent on, in words. A code that never declared a rail
 * predates the model and is universal; an item-scoped one says how many items
 * it covers (the picker is already FILTERED to codes that cover the current
 * selection, so the count is context, not a warning).
 */
function couponRailText(c: CouponOption): string {
  if (c.applies_to_slugs !== null && c.applies_to_slugs.length > 0) {
    return c.applies_to_slugs.length === 1
      ? "1 specific item"
      : `${c.applies_to_slugs.length} specific items`;
  }
  if (c.applies_to === "packages") return "packages";
  if (c.applies_to !== "memberships") return "anything";
  // For a membership the discount runs over whole invoices, and how many is
  // exactly what Stripe's `duration` encodes.
  if (c.duration === "once") return "memberships (first month)";
  if (c.duration === "repeating" && c.duration_in_months !== null) {
    return `memberships (first ${c.duration_in_months} months)`;
  }
  return "memberships (every month)";
}

/**
 * Is this coupon spendable on the CURRENT selection? Mirrors the server's
 * `couponCoversItem` (charge_member enforces it at dispatch since
 * 2026-08-14): class must cover the rail, and an item-scoped code must name
 * the picked slug — custom-priced sales go through CARRIER slugs, so
 * item-scoped codes never apply there. Filtering here is not polish: an
 * unfiltered picker would offer codes the dispatch then rejects.
 */
function couponSpendableOnSelection(
  c: CouponOption,
  remedyType: "package" | "membership",
  isCustom: boolean,
  remedySlug: string,
): boolean {
  const rail = remedyType === "package" ? "packages" : "memberships";
  if (
    (c.applies_to === "packages" || c.applies_to === "memberships") &&
    c.applies_to !== rail
  ) {
    return false;
  }
  if (c.applies_to_slugs !== null && c.applies_to_slugs.length > 0) {
    if (isCustom) return false;
    // No slug picked yet: keep the code visible; the reset effect clears a
    // selection that a later slug pick invalidates.
    if (remedySlug === "") return true;
    return c.applies_to_slugs.includes(remedySlug);
  }
  return true;
}

/**
 * How many times it can still be spent.
 *
 * This deliberately does NOT print Stripe's `duration`, which the picker used
 * to. `duration` answers "how many membership invoices does one redemption
 * discount?", so a one-shot code that happens to cover memberships indefinitely
 * read as "(forever)" — directly contradicting the `Max redemptions: 1` the
 * operator had just typed into the form.
 */
function couponUsesText(c: CouponOption): string {
  const perMember =
    c.max_redemptions_per_member !== null
      ? ` · ${c.max_redemptions_per_member}/member`
      : "";
  if (c.max_redemptions === null) return `unlimited${perMember}`;
  const left = Math.max(0, c.max_redemptions - c.times_redeemed);
  return c.max_redemptions === 1
    ? `1 use${perMember}`
    : `${left} of ${c.max_redemptions} left${perMember}`;
}

/** e.g. "Mario 50% — 50% off · anything · 1 use · member-only". */
function couponLabel(c: CouponOption): string {
  const off =
    c.percent_off !== null
      ? `${c.percent_off}% off`
      : c.amount_off !== null
        ? `${(c.amount_off / 100).toFixed(2)} ${(c.currency ?? "").toUpperCase()} off`
        : "discount";
  const name = c.name ?? c.coupon_id;
  const parts = [off, couponRailText(c), couponUsesText(c)];
  if (c.member_id !== null) parts.push("member-only");
  return `${name} — ${parts.join(" · ")}`;
}

/**
 * Carrier catalog slugs for CUSTOM comp grants. The grant writes a
 * `member_packages` row whose `remaining_sessions` is overridden to the
 * staff-entered count; the slug only carries the studio/home session_type.
 */
export const CUSTOM_PKG_CARRIER_SLUG = {
  studio: "single-session",
  home: "single-home-session",
} as const;

/** Reserved carrier slug for staff-sold CUSTOM memberships. */
export const CUSTOM_MEMBERSHIP_CARRIER_SLUG = "custom-membership";

/** Parse an optional whole-number months term (1–60). Empty → null (no term). */
export function parseMonths(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 1 && n <= 60 ? n : null;
}

/**
 * Parse a custom pack's "Valid for (days)" box: a whole number of days,
 * 1–3650 (the bounds every server schema uses). `null` for anything else,
 * the empty box included — the caller tells the two apart, because an empty
 * box means "let the size rule decide" and a bad one blocks the commit.
 */
export function parseValidityDays(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) &&
    n >= PACKAGE_VALIDITY_DAYS_MIN &&
    n <= PACKAGE_VALIDITY_DAYS_MAX
    ? n
    : null;
}

/**
 * What a custom pack of `credits` on `carrierSlug` lasts by the size rule —
 * the number the "Valid for (days)" box pre-fills with. The SAME function the
 * grant applies when no validity is sent (`resolvePackageValidityDays`), run
 * over the package lookup, so the box shows what the server would do. `null`
 * when the catalogue is not loaded yet, the count is not a valid one, the
 * carrier is missing, or its credits never expire.
 */
export function suggestedCustomValidityDays(
  options: ReadonlyArray<CatalogOption> | null,
  carrierSlug: string,
  credits: number | null,
): number | null {
  if (options === null || credits === null) return null;
  const offering = (o: CatalogOption): PackageValidityOffering | null =>
    typeof o.sessions_total === "number"
      ? {
          sessions_total: o.sessions_total,
          ...(o.validity_days !== undefined
            ? { validity_days: o.validity_days }
            : {}),
          ...(o.session_type !== undefined
            ? { session_type: o.session_type }
            : {}),
        }
      : null;
  const carrier = options.find((o) => o.value === carrierSlug);
  const carrierOffering = carrier === undefined ? null : offering(carrier);
  if (carrierOffering === null) return null;
  const catalogue = options
    .map(offering)
    .filter((o): o is PackageValidityOffering => o !== null);
  return resolvePackageValidityDays(catalogue, carrierOffering, credits);
}

/** Parse a positive-integer credit count from a free-text input. */
export function parseCredits(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isInteger(n) && n > 0 && n <= 200 ? n : null;
}

/** Parse a price in major units (e.g. "65" or "64.50") to minor units (pence). */
export function parsePriceMinor(raw: string): number | null {
  const n = Number(raw.trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  const minor = Math.round(n * 100);
  return minor >= 1 && minor <= 100_000_000 ? minor : null;
}

export interface PurchaseCreditsAutoBook {
  /** The slot to book into after a successful free grant. */
  readonly slotId: string;
  /** Called with the `book_session` run id so the caller can poll it. */
  readonly onBookStarted: (runId: string) => void;
  /** Called when the grant booked synchronously (no run id). */
  readonly onBookedSync: () => void;
}

export function PurchaseCreditsModal({
  memberId,
  headline,
  autoBook,
  onPurchased,
  onClose,
}: {
  readonly memberId: string;
  /**
   * Context line above the form. OMIT when the surrounding surface already
   * says why the form is open — the member page's Buy button does, and "Sell
   * or comp a package or membership for this member" under it only restated
   * the button just pressed. The calendar passes one because there it carries
   * a fact the form cannot: *this member has no credits for this session*.
   */
  readonly headline?: string;
  /** Calendar-only: book into a slot right after a free grant. */
  readonly autoBook?: PurchaseCreditsAutoBook;
  /** Called after a successful credit-adding GRANT (member-page refresh). */
  readonly onPurchased?: () => void;
  /**
   * Dismiss the surrounding modal. Supplied by a modal host (which then sets
   * `hideFooter`), so Close sits on the SAME row as the confirm button rather
   * than in a second footer row beneath it. Omitted where the form is inline
   * and there is nothing to dismiss — the calendar's no-credit remedy.
   */
  readonly onClose?: () => void;
}): JSX.Element {
  const [remedyType, setRemedyType] = useState<"package" | "membership">(
    "package",
  );
  const [pkgOptions, setPkgOptions] =
    useState<ReadonlyArray<CatalogOption> | null>(null);
  const [memOptions, setMemOptions] =
    useState<ReadonlyArray<CatalogOption> | null>(null);
  const [remedySlug, setRemedySlug] = useState("");
  const [remedyBusy, setRemedyBusy] = useState<
    "grant" | "purchase" | "sale" | null
  >(null);
  // How this purchase is settled. Defaults to the Stripe link — the ordinary
  // sale; comping and off-Stripe cash are the exceptions.
  const [payMethod, setPayMethod] = useState<PayMethod>("link");
  // Amount actually taken for a manual sale (catalog mode pre-fills from the
  // offering's list price; editable).
  const [saleAmount, setSaleAmount] = useState("");
  const [remedyMode, setRemedyMode] = useState<"catalog" | "custom">("catalog");
  const [customCredits, setCustomCredits] = useState("");
  const [customPkgType, setCustomPkgType] = useState<"studio" | "home">(
    "studio",
  );
  const [customPrice, setCustomPrice] = useState("");
  const [customMonths, setCustomMonths] = useState("");
  // custom-pack-validity-at-sale — the custom pack's "Valid for (days)" box.
  // Pre-filled by the size rule, and re-filled as the credits or studio/home
  // change, until staff type into it; from then on it is theirs.
  const [customValidity, setCustomValidity] = useState("");
  const [validityEdited, setValidityEdited] = useState(false);
  const [freeRecurring, setFreeRecurring] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // payment-received-credits push 2 (2026-11-04) — a sale `record_manual_sale`
  // refused as the twin of a Stripe payment that added no credits, kept so
  // staff can record it anyway when it really is a second purchase.
  const [twinSaleInput, setTwinSaleInput] = useState<Record<string, unknown> | null>(null);
  const [coupons, setCoupons] = useState<ReadonlyArray<CouponOption> | null>(
    null,
  );
  const [couponId, setCouponId] = useState("");
  // Only codes spendable on the CURRENT selection — charge_member rejects the
  // rest at dispatch, so offering them would be a trap.
  const applicableCoupons =
    coupons === null
      ? null
      : coupons.filter((c) =>
          couponSpendableOnSelection(
            c,
            remedyType,
            remedyMode === "custom",
            remedySlug,
          ),
        );
  // A selection change can filter the chosen code out (picked a package the
  // code doesn't cover, switched rails, went custom) — clear it rather than
  // dispatch a charge that will be refused.
  useEffect(() => {
    if (couponId === "" || applicableCoupons === null) return;
    if (!applicableCoupons.some((c) => c.coupon_id === couponId)) {
      setCouponId("");
    }
  }, [couponId, applicableCoupons]);
  // Copy-to-clipboard state for a freshly created Checkout link.
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyFlashRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyFlashRef.current !== null)
        window.clearTimeout(copyFlashRef.current);
    },
    [],
  );

  /**
   * Put the Checkout URL on the clipboard. On failure the raw link is shown
   * instead — this modal is the ONLY place a created link ever appears, so a
   * silently-blocked copy (insecure context, denied permission) would strand
   * a real Stripe session with no way to reach it.
   */
  const copyPaymentLink = useCallback((): void => {
    if (paymentUrl === null) return;
    const clip = navigator.clipboard;
    if (clip === undefined || typeof clip.writeText !== "function") {
      setCopyFailed(true);
      return;
    }
    void clip.writeText(paymentUrl).then(
      () => {
        setCopyFailed(false);
        setCopied(true);
        if (copyFlashRef.current !== null)
          window.clearTimeout(copyFlashRef.current);
        copyFlashRef.current = window.setTimeout(() => {
          setCopied(false);
          copyFlashRef.current = null;
        }, 1500);
      },
      () => setCopyFailed(true),
    );
  }, [paymentUrl]);

  // Load the codes THIS member may spend (their own, plus the general ones).
  // Scoping is the point: without `member_id` the picker listed every code on
  // the account — including one per lead minted by the welcome flow, which is a
  // per-member code staff could apply to the wrong member. Best-effort: a load
  // failure just leaves the picker empty.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await dispatchAction<
        Record<string, unknown>,
        { coupons: ReadonlyArray<CouponOption> }
      >("list_stripe_coupons", { limit: 100, member_id: memberId });
      if (!cancelled && r.ok) {
        setCoupons(r.response.coupons.filter((c) => c.valid));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const ensureCatalog = useCallback(
    async (type: "package" | "membership") => {
      if (type === "package" && pkgOptions !== null) return;
      if (type === "membership" && memOptions !== null) return;
      try {
        const r = await api.lookupOptions(
          type === "package" ? "packages" : "memberships",
        );
        if (type === "package") setPkgOptions(r.options);
        else setMemOptions(r.options);
      } catch (e) {
        setError(
          `Couldn't load ${type === "package" ? "packages" : "memberships"}: ${(e as Error).message}`,
        );
      }
    },
    [pkgOptions, memOptions],
  );

  // Load the package catalog on first mount (the default selected type).
  useEffect(() => {
    void ensureCatalog("package");
  }, [ensureCatalog]);

  // Pre-fill the validity box from the size rule while staff have not edited
  // it. The package catalogue is the one already loaded on mount.
  useEffect(() => {
    if (validityEdited) return;
    const suggested = suggestedCustomValidityDays(
      pkgOptions,
      CUSTOM_PKG_CARRIER_SLUG[customPkgType],
      parseCredits(customCredits),
    );
    setCustomValidity(suggested === null ? "" : String(suggested));
  }, [validityEdited, pkgOptions, customPkgType, customCredits]);

  // Shared post-credit booking step: when the modal is opened from the
  // calendar's low-credit remedy (`autoBook` set), book the member into the
  // slot the moment credits exist — whether they came from a free grant OR a
  // recorded cash/bank-transfer sale. Returns `true` when it handled the
  // slot (booked, or booking failed with the error surfaced), `false` when
  // there is no slot to book (the member-page flow).
  const bookIntoSlotIfNeeded = useCallback(async (): Promise<boolean> => {
    if (autoBook === undefined) return false;
    const b = await dispatchAction<
      Record<string, unknown>,
      { run_id?: string }
    >("book_session", { slot_id: autoBook.slotId, member_id: memberId });
    if (b.ok) {
      const rid = b.response?.run_id;
      if (typeof rid === "string") autoBook.onBookStarted(rid);
      else autoBook.onBookedSync();
    } else {
      setError(`Credits added, but booking failed: ${b.message || b.code}`);
    }
    return true;
  }, [autoBook, memberId]);

  // Grant a free (comp) package OR membership; if `autoBook` is set, book into
  // the slot right after.
  const grantCredits = useCallback(async () => {
    let grantInput: Record<string, unknown>;
    if (remedyMode === "custom") {
      const n = parseCredits(customCredits);
      if (n === null) {
        setError("Enter a whole number of credits (1–200).");
        return;
      }
      if (remedyType === "package") {
        grantInput = {
          action: "staff_create_member_package",
          member_id: memberId,
          package_slug: CUSTOM_PKG_CARRIER_SLUG[customPkgType],
          source: "comp",
          remaining_sessions_override: n,
        };
      } else if (freeRecurring) {
        grantInput = {
          action: "grant_recurring_membership",
          member_id: memberId,
          membership_slug: CUSTOM_MEMBERSHIP_CARRIER_SLUG,
          sessions_per_cycle: n,
        };
      } else {
        grantInput = {
          action: "staff_create_member_membership",
          member_id: memberId,
          membership_slug: CUSTOM_MEMBERSHIP_CARRIER_SLUG,
          remaining_sessions_override: n,
        };
      }
    } else {
      if (remedySlug === "") {
        setError(`Pick a ${remedyType} to grant.`);
        return;
      }
      grantInput =
        remedyType === "package"
          ? {
              action: "staff_create_member_package",
              member_id: memberId,
              package_slug: remedySlug,
              source: "comp",
            }
          : {
              action: "staff_create_member_membership",
              member_id: memberId,
              membership_slug: remedySlug,
            };
    }
    setRemedyBusy("grant");
    setError(null);
    try {
      const { action, ...payload } = grantInput as {
        action: string;
      } & Record<string, unknown>;
      const g = await dispatchAction(action, payload);
      if (!g.ok) {
        setRemedyBusy(null);
        setError(`Grant failed: ${g.message || g.code}`);
        return;
      }
      // Credits now exist. If opened from the calendar, book into the slot.
      const handled = await bookIntoSlotIfNeeded();
      setRemedyBusy(null);
      // Member-page flow: just added credits.
      if (!handled) onPurchased?.();
    } catch (e) {
      setRemedyBusy(null);
      setError((e as Error).message);
    }
  }, [
    memberId,
    remedyType,
    remedySlug,
    remedyMode,
    customCredits,
    customPkgType,
    freeRecurring,
    bookIntoSlotIfNeeded,
    onPurchased,
  ]);

  // Book now, pay later: grant an `unpaid` package carrying the amount owed,
  // then — from the calendar — book straight into the slot. The credit is a
  // normal usable credit (the booking path is unchanged); what makes it a debt
  // is `source='unpaid'` + `metadata.owed_minor`, cleared later by
  // `settle_member_package_debt` from the member page.
  //
  // Deliberately NOT a comp: a comp asserts the studio gave the session away
  // and prices to £0 in payroll for ever, which is a different (and wrong)
  // statement about a member who simply has not paid yet.
  const grantUnpaid = useCallback(async () => {
    const owedMinor = parsePriceMinor(
      remedyMode === "custom" ? customPrice : saleAmount,
    );
    if (owedMinor === null) {
      setError("Enter the amount owed (e.g. 65 or 64.50).");
      return;
    }
    let input: Record<string, unknown>;
    if (remedyMode === "custom") {
      const credits = parseCredits(customCredits);
      if (credits === null) {
        setError("Enter a whole number of credits (1–200).");
        return;
      }
      input = {
        member_id: memberId,
        package_slug: CUSTOM_PKG_CARRIER_SLUG[customPkgType],
        source: "unpaid",
        remaining_sessions_override: credits,
        owed_minor: owedMinor,
      };
    } else {
      if (remedySlug === "") {
        setError("Pick a package to book against.");
        return;
      }
      input = {
        member_id: memberId,
        package_slug: remedySlug,
        source: "unpaid",
        owed_minor: owedMinor,
      };
    }
    setRemedyBusy("grant");
    setError(null);
    try {
      const g = await dispatchAction("staff_create_member_package", input);
      if (!g.ok) {
        setRemedyBusy(null);
        setError(`Couldn't book unpaid: ${g.message || g.code}`);
        return;
      }
      const handled = await bookIntoSlotIfNeeded();
      setRemedyBusy(null);
      if (!handled) onPurchased?.();
    } catch (e) {
      setRemedyBusy(null);
      setError((e as Error).message);
    }
  }, [
    memberId,
    remedyMode,
    remedySlug,
    saleAmount,
    customPrice,
    customCredits,
    customPkgType,
    bookIntoSlotIfNeeded,
    onPurchased,
  ]);

  // Dispatch a built sale, then the shared post-credit step. The twin refusal
  // (`manual_sale_matches_stripe_charge`: the member has a Stripe payment for
  // the same money that added no credits) keeps the input for "Record it
  // anyway", which sends it again with `allow_duplicate`.
  const submitSale = useCallback(
    async (input: Record<string, unknown>): Promise<void> => {
      setRemedyBusy("sale");
      setError(null);
      setPaymentUrl(null);
      setTwinSaleInput(null);
      try {
        const s = await dispatchAction("record_manual_sale", input);
        if (!s.ok) {
          setRemedyBusy(null);
          if (s.code === "manual_sale_matches_stripe_charge") setTwinSaleInput(input);
          setError(`Sale failed: ${s.message || s.code}`);
          return;
        }
        const handled = await bookIntoSlotIfNeeded();
        setRemedyBusy(null);
        if (!handled) onPurchased?.();
      } catch (e) {
        setRemedyBusy(null);
        setError((e as Error).message);
      }
    },
    [bookIntoSlotIfNeeded, onPurchased],
  );

  // Record a manual off-Stripe SALE (cash / bank transfer / other): writes
  // the payment AND grants the purchased credit in one canonical action
  // (`record_manual_sale`), then — if opened from the calendar — books the
  // slot. Catalog mode sells the picked offering at the (editable) amount
  // taken; Custom mode sells an ad-hoc credit count at the custom price.
  const recordSale = useCallback(async () => {
    if (!isManualPayMethod(payMethod)) return;
    let input: Record<string, unknown>;
    if (remedyMode === "custom") {
      const credits = parseCredits(customCredits);
      const priceMinor = parsePriceMinor(customPrice);
      const validityDays = parseValidityDays(customValidity);
      if (credits === null) {
        setError("Enter a whole number of credits (1–200).");
        return;
      }
      if (priceMinor === null) {
        setError("Enter the amount paid (e.g. 65 or 64.50).");
        return;
      }
      if (customValidity.trim() !== "" && validityDays === null) {
        setError("Enter how many days the credits last (1–3650).");
        return;
      }
      input = {
        member_id: memberId,
        kind: remedyType,
        slug:
          remedyType === "package"
            ? CUSTOM_PKG_CARRIER_SLUG[customPkgType]
            : CUSTOM_MEMBERSHIP_CARRIER_SLUG,
        payment_method: payMethod,
        amount_minor: priceMinor,
        currency: "GBP",
        occurred_at: new Date().toISOString(),
        sessions_override: credits,
        // A custom PACK carries its validity; an empty box sends none and the
        // server's size rule decides (the same number the box pre-filled).
        ...(remedyType === "package" && validityDays !== null
          ? { validity_days: validityDays }
          : {}),
      };
    } else {
      if (remedySlug === "") {
        setError(`Pick a ${remedyType} to sell.`);
        return;
      }
      const priceMinor = parsePriceMinor(saleAmount);
      if (priceMinor === null) {
        setError("Enter the amount paid (e.g. 65 or 64.50).");
        return;
      }
      const opt = (
        (remedyType === "package" ? pkgOptions : memOptions) ?? []
      ).find((o) => o.value === remedySlug);
      input = {
        member_id: memberId,
        kind: remedyType,
        slug: remedySlug,
        payment_method: payMethod,
        amount_minor: priceMinor,
        currency: (opt?.currency ?? "GBP").toUpperCase(),
        occurred_at: new Date().toISOString(),
      };
    }
    await submitSale(input);
  }, [
    memberId,
    remedyType,
    remedySlug,
    remedyMode,
    customCredits,
    customPkgType,
    customPrice,
    customValidity,
    payMethod,
    saleAmount,
    pkgOptions,
    memOptions,
    submitSale,
  ]);

  // Create a Stripe checkout link for the chosen package OR membership.
  const createPaymentLink = useCallback(async () => {
    let input: Record<string, unknown>;
    if (remedyMode === "custom") {
      const credits = parseCredits(customCredits);
      const priceMinor = parsePriceMinor(customPrice);
      if (credits === null) {
        setError("Enter a whole number of credits (1–200).");
        return;
      }
      if (priceMinor === null) {
        setError("Enter a price (e.g. 65 or 64.50).");
        return;
      }
      if (remedyType === "package") {
        const validityDays = parseValidityDays(customValidity);
        if (customValidity.trim() !== "" && validityDays === null) {
          setError("Enter how many days the credits last (1–3650).");
          return;
        }
        input = {
          member_id: memberId,
          package_slug: CUSTOM_PKG_CARRIER_SLUG[customPkgType],
          amount_minor: priceMinor,
          currency: "gbp",
          sessions_override: credits,
          description: `${credits} ${customPkgType} credit${credits === 1 ? "" : "s"}`,
          // custom-pack-validity-at-sale — rides the link to the Stripe
          // metadata and the grant; an empty box leaves it to the size rule.
          ...(validityDays !== null ? { validity_days: validityDays } : {}),
        };
      } else {
        const months = parseMonths(customMonths);
        const termText =
          months !== null
            ? ` for ${months} month${months === 1 ? "" : "s"}`
            : "";
        input = {
          member_id: memberId,
          membership_slug: CUSTOM_MEMBERSHIP_CARRIER_SLUG,
          amount_minor: priceMinor,
          currency: "gbp",
          sessions_override: credits,
          description: `Custom membership — ${credits} credit${credits === 1 ? "" : "s"}/month${termText}`,
          // custom-membership-fixed-term (2026-10-29) — the term as a FIELD,
          // not only as words in the description: it reaches the Stripe
          // subscription's metadata and the membership row, and Stripe is
          // given the end date so it stops billing after these months.
          ...(months !== null ? { term_months: months } : {}),
        };
      }
    } else {
      if (remedySlug === "") {
        setError(`Pick a ${remedyType} first.`);
        return;
      }
      input =
        remedyType === "package"
          ? { member_id: memberId, package_slug: remedySlug }
          : { member_id: memberId, membership_slug: remedySlug };
    }
    // Apply the chosen Stripe coupon as a Checkout discount, if any.
    if (couponId !== "") {
      input = { ...input, coupon: couponId };
    }
    setRemedyBusy("purchase");
    setError(null);
    setPaymentUrl(null);
    try {
      // A DURABLE link (2026-08-20), not a raw Checkout Session. `charge_member`
      // mints a Session that dies in 60 minutes, so a link a member opened the
      // next morning was already dead — the reason staff links kept needing to
      // be re-sent. `create_payment_link` returns a 30-day link of our own and
      // mints a fresh Session on every tap; both actions return `payment_url`,
      // so everything downstream of here is unchanged.
      //
      // Registration moves BEFORE the mint, and stops being an error handler.
      // Minting touches no vendor, so it cannot fail for want of a Customer —
      // the TAP would fail instead, which for a link that lives 30 days means
      // handing staff something that looks fine and is dead every time the
      // member tries it. `register_stripe_customer` is explicitly idempotent
      // and no-ops when the vendor_links row already exists, so paying that
      // cost up front is what makes the link good for its whole life.
      const reg = await dispatchAction<
        { member_id: string },
        { stripe_customer_id?: string }
      >("register_stripe_customer", { member_id: memberId });
      if (!reg.ok) {
        setRemedyBusy(null);
        setError(
          `Couldn't register the member with Stripe: ${reg.message || reg.code}`,
        );
        return;
      }
      const c = await dispatchAction<
        Record<string, unknown>,
        { payment_url?: string }
      >("create_payment_link", input);
      setRemedyBusy(null);
      if (c.ok) {
        const url = c.response?.payment_url ?? null;
        setPaymentUrl(url);
        // A second link within the flash window must not inherit "Copied ✓".
        setCopied(false);
        setCopyFailed(false);
        if (url === null) {
          setError("Payment link created but no URL was returned.");
        } else {
          // A paid purchase adds no credits until the member pays; still let
          // the host know a sale flow completed (no-op refresh is harmless).
          onPurchased?.();
        }
      } else {
        setError(`Payment link failed: ${c.message || c.code}`);
      }
    } catch (e) {
      setRemedyBusy(null);
      setError((e as Error).message);
    }
  }, [
    memberId,
    remedyType,
    remedySlug,
    remedyMode,
    customCredits,
    customPrice,
    customPkgType,
    customMonths,
    customValidity,
    // Load-bearing: the callback READS couponId, so omitting it here froze the
    // coupon at whatever was selected when the callback was last rebuilt —
    // usually "" — and the link went out at full price with no error anywhere.
    couponId,
    onPurchased,
  ]);

  // What the one confirm button does, and says, for the chosen method.
  const confirmLabel =
    payMethod === "link"
      ? remedyBusy === "purchase"
        ? "Creating…"
        : "Create link"
      : payMethod === "free"
        ? remedyBusy === "grant"
          ? "Granting…"
          : remedyMode === "custom" &&
              remedyType === "membership" &&
              freeRecurring
            ? "Grant free recurring"
            : "Grant credits"
        : payMethod === "unpaid"
          ? remedyBusy === "grant"
            ? "Booking…"
            : "Book — owes"
          : remedyBusy === "sale"
            ? "Recording…"
            : "Record sale";

  const confirm = (): void => {
    if (payMethod === "link") void createPaymentLink();
    else if (payMethod === "free") void grantCredits();
    else if (payMethod === "unpaid") void grantUnpaid();
    else void recordSale();
  };

  // Every method needs SOMETHING picked or typed before it can commit. The
  // amount is required wherever real money is involved (a link's price, a
  // recorded sale's take, the balance an unpaid booking leaves owing) and
  // irrelevant to a comp.
  const creditsOk = parseCredits(customCredits) !== null;
  const priceOk = parsePriceMinor(customPrice) !== null;
  // The validity box shows for a SOLD custom pack: by link or recorded as a
  // cash / bank / other sale. A comp and a book-now-pay-later debt never
  // expire by their own rules, so they get no box.
  const showsValidity =
    remedyMode === "custom" &&
    remedyType === "package" &&
    (payMethod === "link" || isManualPayMethod(payMethod));
  const validityOk =
    !showsValidity ||
    customValidity.trim() === "" ||
    parseValidityDays(customValidity) !== null;
  const needsCatalogAmount =
    isManualPayMethod(payMethod) || payMethod === "unpaid";
  const confirmDisabled =
    remedyBusy !== null ||
    (remedyMode === "catalog"
      ? remedySlug === "" ||
        (needsCatalogAmount && parsePriceMinor(saleAmount) === null)
      : !creditsOk || (payMethod !== "free" && !priceOk) || !validityOk);

  return (
    <div className="cal-remedy">
      {headline !== undefined ? (
        <p className="cal-remedy-head">{headline}</p>
      ) : null}

      {/* 1 — WHAT is being sold. */}
      <section className="buy-section">
        <h4 className="buy-section-title">Package or membership</h4>
        <div className="buy-seg" role="group" aria-label="Package or membership">
          {(["package", "membership"] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`buy-seg-btn${remedyType === t ? " is-active" : ""}`}
              aria-pressed={remedyType === t}
              onClick={() => {
                setRemedyType(t);
                setRemedySlug("");
                setPaymentUrl(null);
                // Switching to a membership while a manual method is chosen
                // would leave an unreachable selection active.
                if (!payMethodsFor(t).includes(payMethod)) setPayMethod("link");
                void ensureCatalog(t);
              }}
            >
              {t === "package" ? "Package" : "Membership"}
            </button>
          ))}
        </div>
      </section>

      {/* 2 — WHICH one: a catalog offering, or an ad-hoc count/price. */}
      <section className="buy-section">
        <h4 className="buy-section-title">Catalog or custom</h4>
        <div className="buy-seg" role="group" aria-label="Catalog or custom">
          {(["catalog", "custom"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`buy-seg-btn${remedyMode === m ? " is-active" : ""}`}
              aria-pressed={remedyMode === m}
              onClick={() => {
                setRemedyMode(m);
                setPaymentUrl(null);
              }}
            >
              {m === "catalog" ? "Catalog" : "Custom"}
            </button>
          ))}
        </div>

        {remedyMode === "catalog" ? (
          <select
            className="cal-book-select cal-remedy-pkg"
            value={remedySlug}
            onChange={(e) => {
              const v = e.target.value;
              setRemedySlug(v);
              setPaymentUrl(null);
              // Pre-fill the manual-sale amount from the offering's list price
              // (editable — staff may have taken a different amount).
              const opt = (
                (remedyType === "package" ? pkgOptions : memOptions) ?? []
              ).find((o) => o.value === v);
              if (typeof opt?.price_minor === "number") {
                setSaleAmount((opt.price_minor / 100).toString());
              }
            }}
          >
            <option value="">Choose a {remedyType}…</option>
            {((remedyType === "package" ? pkgOptions : memOptions) ?? []).map(
              (o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ),
            )}
          </select>
        ) : (
          <div className="cal-remedy-custom">
            <input
              type="number"
              min={1}
              max={200}
              className="cal-remedy-credits"
              placeholder={
                remedyType === "package" ? "Credits" : "Credits per month"
              }
              value={customCredits}
              onChange={(e) => {
                setCustomCredits(e.target.value);
                setPaymentUrl(null);
              }}
            />
            {remedyType === "package" ? (
              <select
                className="cal-remedy-pkgtype"
                value={customPkgType}
                onChange={(e) =>
                  setCustomPkgType(
                    e.target.value === "home" ? "home" : "studio",
                  )
                }
              >
                <option value="studio">Studio</option>
                <option value="home">Home</option>
              </select>
            ) : null}
            {/* A comp has no price — under a free grant this field is ignored
                by every code path, so showing it only invites staff to type a
                number that goes nowhere. */}
            {payMethod === "free" ? null : (
              <input
                type="number"
                min={0}
                step="0.01"
                className="cal-remedy-credits"
                placeholder={
                  payMethod === "unpaid"
                    ? "£ owed"
                    : remedyType === "package"
                      ? "£ price"
                      : "£ / month"
                }
                value={customPrice}
                onChange={(e) => {
                  setCustomPrice(e.target.value);
                  setPaymentUrl(null);
                }}
              />
            )}
            {remedyType === "membership" ? (
              <input
                type="number"
                min={1}
                max={60}
                step="1"
                className="cal-remedy-credits"
                placeholder="Months (optional)"
                value={customMonths}
                onChange={(e) => {
                  setCustomMonths(e.target.value);
                  setPaymentUrl(null);
                }}
              />
            ) : null}
          </div>
        )}
      </section>

      {/* 3 — HOW it is paid for. Five peers, one commit. */}
      <section className="buy-section">
        <h4 className="buy-section-title">Payment</h4>
        <div className="buy-methods" role="group" aria-label="Payment method">
          {payMethodsFor(remedyType).map((m) => (
            <button
              key={m}
              type="button"
              className={`buy-method${payMethod === m ? " is-active" : ""}`}
              aria-pressed={payMethod === m}
              onClick={() => {
                setPayMethod(m);
                setPaymentUrl(null);
              }}
            >
              {payMethodLabel(m, remedyType)}
            </button>
          ))}
        </div>

        {/* Only the chosen method's own field, so the section stays one
            question deep rather than showing every method's inputs at once. */}
        {payMethod === "link" &&
        applicableCoupons !== null &&
        applicableCoupons.length > 0 ? (
          <label className="cal-remedy-coupon">
            <span>Coupon</span>
            <select
              value={couponId}
              onChange={(e) => setCouponId(e.target.value)}
            >
              <option value="">No coupon</option>
              {applicableCoupons.map((c) => (
                <option key={c.coupon_id} value={c.coupon_id}>
                  {couponLabel(c)}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {showsValidity ? (
          <label
            className="cal-remedy-validity"
            title="How long the credits last from the day they are granted. Pre-filled with what a catalogue pack of this size lasts; change it if you agreed something else with the member."
          >
            <span>Valid for (days)</span>
            <input
              type="number"
              min={PACKAGE_VALIDITY_DAYS_MIN}
              max={PACKAGE_VALIDITY_DAYS_MAX}
              step="1"
              className="buy-amount"
              value={customValidity}
              onChange={(e) => {
                setCustomValidity(e.target.value);
                setValidityEdited(true);
                setPaymentUrl(null);
              }}
            />
          </label>
        ) : null}

        {payMethod === "free" &&
        remedyType === "membership" &&
        remedyMode === "custom" ? (
          <label
            className="cal-remedy-recurring"
            title="A free recurring membership is a real £0 Stripe subscription that auto-refills the credits each month. Unchecked = a one-time free grant that never refills. Recurring needs the member to have a Stripe customer."
          >
            <input
              type="checkbox"
              checked={freeRecurring}
              onChange={(e) => setFreeRecurring(e.target.checked)}
            />{" "}
            Auto-refill monthly (free recurring)
          </label>
        ) : null}

        {needsCatalogAmount && remedyMode === "catalog" ? (
          <input
            type="number"
            min={0}
            step="0.01"
            className="buy-amount"
            placeholder={
              payMethod === "unpaid" ? "£ amount owed" : "£ amount paid"
            }
            value={saleAmount}
            onChange={(e) => setSaleAmount(e.target.value)}
          />
        ) : null}

        {payMethod === "unpaid" ? (
          <p className="cal-remedy-note">
            Books the session now and records the balance as owed. Settle it
            later from the member&apos;s page — cash, an existing payment, or
            written off.
          </p>
        ) : null}

        <div className="buy-confirm-row">
          {/* Dismiss left, commit right — pushed apart so the two aren't
              adjacent, since a mis-click either way is annoying to undo. */}
          {onClose !== undefined ? (
            <button
              type="button"
              className="action-dispatch-btn buy-close"
              onClick={onClose}
            >
              Close
            </button>
          ) : null}
          <button
            className="buy-confirm"
            disabled={confirmDisabled}
            title={
              payMethod === "link" &&
              remedyMode === "custom" &&
              remedyType === "membership"
                ? "Recurring subscription · billed monthly until cancelled"
                : isManualPayMethod(payMethod)
                  ? "Record a payment taken outside Stripe and grant the credit"
                  : undefined
            }
            onClick={confirm}
          >
            {confirmLabel}
          </button>
        </div>

        {/* The created link, as one button — the raw URL is ~500 characters of
            Stripe session state that nobody reads, and printing it filled the
            modal with noise where the only useful gesture was "copy it". */}
        {paymentUrl !== null ? (
          <>
            <button
              type="button"
              className="buy-copy-link"
              onClick={copyPaymentLink}
            >
              {copied ? "Copied ✓" : "Copy payment link"}
            </button>
            {copyFailed ? (
              <p className="cal-remedy-link">
                Couldn&apos;t copy — the link is{" "}
                <a href={paymentUrl} target="_blank" rel="noreferrer noopener">
                  {paymentUrl}
                </a>
              </p>
            ) : null}
          </>
        ) : null}
        {error !== null ? <p className="cal-action-error">{error}</p> : null}
        {twinSaleInput !== null ? (
          <p className="cal-remedy-link">
            <button
              type="button"
              className="row-action-btn"
              disabled={remedyBusy !== null}
              onClick={() => void submitSale({ ...twinSaleInput, allow_duplicate: true })}
              title="Records this sale as well. Use it only when the member really bought twice; otherwise add the credits from the Stripe payment."
            >
              Record it anyway — it is a second purchase
            </button>
          </p>
        ) : null}
      </section>
    </div>
  );
}
